/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * CurrencyInitializer Component
 * ============================================================================
 *
 * Initializes the currency cache from API when the user is authenticated.
 * This enables proper currency formatting across the application.
 *
 * Usage: Place this component inside a provider that has access to React Query
 * and the auth store. It will automatically fetch currencies and populate
 * the cache when an authenticated user is present.
 *
 * ============================================================================
 */

import { useEffect } from 'react';
import { useCurrencies } from '@/hooks/api/useCurrencies';
import { setCurrencyCache } from '@/lib/utils';
import { useAuthStore } from '@/stores/authStore';

/**
 * Component that initializes the currency cache when user is authenticated
 * Render this component once in your app (e.g., in main.tsx or App.tsx)
 */
export function CurrencyInitializer(): null {
  const { isAuthenticated } = useAuthStore();

  // Only fetch currencies when authenticated
  const { data: currencies } = useCurrencies({ enabled: isAuthenticated });

  useEffect(() => {
    if (currencies && currencies.length > 0) {
      setCurrencyCache(currencies);
    }
  }, [currencies]);

  // This component renders nothing
  return null;
}

export default CurrencyInitializer;
